import React, { useState, useEffect, Suspense } from 'react';
import { motion } from 'framer-motion';
import {  useParams, useNavigate } from 'react-router-dom';
import { FaSearch, FaClock, FaSortAmountUp,FaSortAmountDown, FaArrowLeft } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';
import { blogPosts } from '../utils/blogPosts';

// BlogCard Component
const BlogCard = ({ post, onClick }) => (
  <motion.div
    whileHover={{ y: -5 }}
    onClick={onClick}
    className="bg-white/10 rounded-xl overflow-hidden cursor-pointer shadow-lg hover:shadow-xl transition-shadow"
  >
    <img
      src={post.image}
      alt={post.title}
      className="w-full h-48 object-cover"
      loading="lazy"
      onError={(e) => {
        e.target.src = '/default-post-image.jpg';
      }}
    />
    <div className="p-6">
      <h2 className="text-2xl font-semibold text-white">{post.title}</h2>
      <p className="text-gray-400 text-sm mt-2">{post.excerpt}</p>
      <div className="flex items-center space-x-2 text-gray-500 text-sm mt-4">
        <FaClock />
        <span>{post.readTime}</span>
      </div>
      <div className="flex items-center space-x-2 text-gray-500 text-sm mt-2">
        <img
          src={post.author.avatar}
          alt={`${post.author.name}'s avatar`}
          className="w-8 h-8 rounded-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = 'https://res.cloudinary.com/dqovjmmlx/image/upload/f_auto,q_auto/D20250121_142959_qwuz9k';
          }}
        />
        <span>{post.author.name}</span>
      </div>
    </div>
  </motion.div>
);

// BlogDetail Component
const BlogDetail = ({ post, onBack }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="max-w-3xl mx-auto px-4"
  >
    <button
      onClick={onBack}
      className="flex items-center space-x-2 text-blue-400 mb-8"
    >
      <FaArrowLeft />
      <span>Back to Posts</span>
    </button>

    <img
      src={post.image}
      alt={post.title}
      className="w-full h-64 object-cover rounded-xl mb-8"
    />

    <h1 className="text-4xl font-bold text-white mb-6">{post.title}</h1>

    <div className="prose prose-invert max-w-none">
      {post.content}
    </div>

    <div className="mt-8 flex flex-wrap gap-2">
      {post.tags.map(tag => (
        <span
          key={tag}
          className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm"
        >
          {tag}
        </span>
      ))}
    </div>
  </motion.div>
);

// BlogGrid Component
const BlogGrid = ({ posts, onPostClick }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 z-50 gap-8">
    {posts.map(post => (
      <BlogCard
        key={post.id}
        post={post}
        onClick={() => onPostClick(post)}
      />
    ))}
  </div>
);

// Blog Component
const Blog = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedPost, setSelectedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 6;

  useEffect(() => {
    if (id) {
      const post = blogPosts.find(p => p.id === parseInt(id));
      if (post) {
        setSelectedPost(post);
      } else {
        navigate('/blog');
      }
    }
    setLoading(false);
  }, [id, navigate]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handleSort = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const filteredPosts = blogPosts.filter(post =>
    post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const sortedPosts = filteredPosts.sort((a, b) => {
    if (sortOrder === 'asc') {
      return new Date(a.date) - new Date(b.date);
    } else {
      return new Date(b.date) - new Date(a.date);
    }
  });

  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = sortedPosts.slice(indexOfFirstPost, indexOfLastPost);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <>
      <Helmet>
        <title>{selectedPost ? selectedPost.title : 'Blog'} | Farhan's Portfolio</title>
        <meta name="author" content="Mahmudur Rahman" />
        <meta property="og:image" content="https://www.sevenstepstowealth.com/wp-content/uploads/2019/09/blog-banner.png" />
        <meta property="twitter:image" content="https://pbs.twimg.com/profile_banners/1266042540895072258/1734718719/1500x500" />
        <link rel="canonical" href="https://devplus.fun/blog" />
        <meta name="description" content={selectedPost ? selectedPost.excerpt : 'Read my latest blog posts about web development and technology.'} />
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": "Mahmud's Portfolio Blog",
          "description": "Read my latest blog posts about web development and technology.",
          "url": "https://devplus.fun/blog",
          "image":"https://pbs.twimg.com/profile_banners/1266042540895072258/1734718719/1500x500",
          "author": {
            "@type": "Person",
            "name": "Mahmudur Rahman"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Mahmud's Portfolio",
            "logo": {
              "@type": "ImageObject",
              "url": "https://www.sevenstepstowealth.com/wp-content/uploads/2019/09/blog-banner.png"
            }
          },
          "datePublished": selectedPost ? selectedPost.date : "2025-01-01",
          "dateModified": selectedPost ? selectedPost.date : "2025-02-01",
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://devplus.fun/blog"
          }
        })}
      </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white">
        
        <div className="container mx-auto px-4 py-16">
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
            </div>
          ) : selectedPost ? (
            <BlogDetail post={selectedPost} onBack={() => navigate('/blog')} />
          ) : (
            < >
             
              
              <div className="flex flex-col sm:flex-row justify-between items-center mt-16 mb-16 gap-2">
              <button
                  onClick={handleSort}
                  className="flex items-center space-x-2 bg-white/10 px-4 py-2 rounded-lg hover:bg-white/20 transition-colors"
                >
                  {sortOrder === 'asc' ? <FaSortAmountDown /> : <FaSortAmountUp />}
                  <span>Sort by Date</span>
                </button>
                <div className="relative w-full sm:w-1/3 mb-4 sm:mb-0">
                  <input
                    type="text"
                    placeholder="Search posts..."
                    value={searchQuery}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 bg-white/10 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <FaSearch className="absolute left-3 top-3 text-gray-400" />
                </div>
               
              </div>

              <BlogGrid posts={currentPosts} onPostClick={(post) => navigate(`/blog/${post.id}`)} />

              <div className="flex justify-center mt-16 mb-8">
                {Array.from({ length: Math.ceil(filteredPosts.length / postsPerPage) }, (_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => paginate(i + 1)}
                    className={`mx-1 px-4 py-2 rounded-lg ${
                      currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-white/10 hover:bg-white/20'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Blog;